import { useMemo, useState } from 'react'
import {
  AlertIcon,
  ChildrenIcon,
  DataSurface,
  SearchIcon,
} from '../../../shared/components'
import { useResource } from '../../../shared/hooks/useResource'
import { getAllStudents, type StudentRow } from '../../../shared/api/classes'
import type { StaffAuth } from '../../../shared/api/teaching'
import { TEACHER_COPY, describeError, type Lang } from './copy'
import { STUDENT_COPY, BAND_LABEL } from './studentCopy'
import { describeLastPlayed } from './lastPlayed'

/**
 * Every child this teacher has, grouped by grade and section.
 *
 * 🔴 ONE REQUEST FOR THE WHOLE SCREEN. The obvious build is one roster call per
 * section, which is a round trip per class and grows with the timetable —
 * `architecture.md` §12's *"25 queries for one dashboard"* moved into the
 * browser. `GET /students` returns every child the caller may see with their
 * class attached, in one scoped query, already ordered grade → section → roll.
 *
 * 🔴 THE GROUPING IS PRESENTATION; THE ORDER IS THE SERVER'S. Rows arrive
 * sorted, and this file only decides where the headings fall. Re-sorting here
 * would be a second answer to *"which child is first"*, and the two would
 * disagree the day either changed.
 *
 * ⚠️ IT IS A SEPARATE SCREEN FROM D2's ROSTER, WHICH LOOKS SIMILAR AND IS NOT.
 * D2 is Play Mode: one class, and every row is a **launch** button, because a
 * teacher there is standing next to a child holding a laptop. This is the
 * reading surface — every class at once, and a row opens the child's page. Merging
 * them would put a "start a game" button on a screen used after school.
 */

export interface StudentsScreenProps {
  lang?: Lang
  auth?: StaffAuth
  classId?: string
  /*
   * ⚠️ NO `className` — the shell's header carries the open section, so a prop
   * threaded down for a heading this screen no longer draws is a second answer
   * to the same question. `HomeworkScreen` records the same removal.
   */
  onOpenChild: (playerId: string, classId: string, name: string) => void
}

export function StudentsScreen({ lang = 'en', auth, classId, onOpenChild }: StudentsScreenProps) {
  const t = TEACHER_COPY[lang]
  const s = STUDENT_COPY[lang]
  const [query, setQuery] = useState('')

  const resource = useResource<StudentRow[]>(
    (signal) => getAllStudents({ ...auth, signal }),
    ['all-students'],
    { isEmpty: (rows) => rows.length === 0 },
  )

  const filteredData = useMemo(() => {
    const data = resource.data ?? []
    if (!classId) return data
    return data.filter((st) => st.classId === classId)
  }, [resource.data, classId])

  const groups = useMemo(() => groupStudents(filteredData, query), [filteredData, query])
  const total = filteredData.length
  const shown = groups.reduce((n, g) => n + g.students.length, 0)

  return (
    <section className="font-dash text-dash-sm text-ink max-w-5xl mx-auto pb-12">
      {/* ── Search Input ── */}
      <div className="mb-6 relative max-w-xs">
        <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted">
          <SearchIcon size={16} />
        </span>
        <input
          className="w-full rounded-pill border border-line bg-surface py-2 pr-4 pl-9 font-dash text-dash-xs font-medium text-ink shadow-sm outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary-tint"
          placeholder={t.search}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <DataSurface
        state={resource.state}
        skeletonRows={8}
        emptyMessage={t.emptyClass}
        errorMessage={describeError(resource.error, lang)}
        errorLastWorked={
          resource.lastLoadedAt
            ? t.lastWorked(resource.lastLoadedAt.toLocaleTimeString())
            : t.neverWorked
        }
        errorRetryLabel={t.retry}
        onRetry={resource.retry}
      >
        {shown === 0 ? (
          <p className="text-muted font-medium">{t.noMatch}</p>
        ) : (
          <>
            {query.trim() !== '' && (
              <p className="mb-4 text-dash-xs font-medium text-muted">{t.showingOf(shown, total)}</p>
            )}

            {groups.map((group) => (
              <div key={group.key} className="mb-8">
                {/* Class Heading & Count Badge */}
                <div className="flex items-center gap-2.5 mb-3">
                  <h2 className="font-dash text-dash-md font-extrabold text-ink">{group.heading}</h2>
                  <span className="rounded-full bg-primary-tint text-primary px-2.5 py-0.5 font-dash text-dash-xs font-bold shadow-sm">
                    {group.students.length}
                  </span>
                </div>

                {/* Students Table Card */}
                <div className="rounded-2xl border border-line bg-surface overflow-hidden shadow-card">
                  <table className="w-full border-collapse text-left font-dash text-dash-xs">
                    <thead>
                      <tr className="bg-[#fffdf5] border-b border-line text-ink font-extrabold">
                        <th className="px-5 py-3.5">{t.roll}</th>
                        <th className="px-5 py-3.5">{t.student}</th>
                        <th className="px-5 py-3.5">{s.band}</th>
                        <th className="px-5 py-3.5">{t.parent}</th>
                        <th className="px-5 py-3.5">{t.lastPlayed}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line/60">
                      {group.students.map((row) => (
                        <tr
                          key={row.playerId}
                          onClick={() => onOpenChild(row.playerId, row.classId, row.name)}
                          className="hover:bg-canvas/60 transition-colors cursor-pointer"
                        >
                          {/* Roll Number */}
                          <td className="px-5 py-4 font-extrabold text-ink">
                            {row.rollNumber ?? s.dash}
                          </td>

                          {/* Student Name & Avatar */}
                          <td className="px-5 py-4 font-extrabold text-ink">
                            <div className="flex items-center gap-3">
                              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-tint text-primary text-sm font-bold border border-pink-100 shrink-0">
                                {row.name.charAt(0)}
                              </span>
                              <span>{row.name}</span>
                            </div>
                          </td>

                          {/* Age Band */}
                          <td className="px-5 py-4 font-medium text-muted">
                            {BAND_LABEL[row.ageBand] ?? row.ageBand}
                          </td>

                          {/* Parent Link Status */}
                          <td className="px-5 py-4">
                            {row.parentName ? (
                              <span className="font-medium text-ink">{row.parentName}</span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 rounded-pill border border-pill-warning-line bg-pill-warning-bg px-3.5 py-1 font-dash text-dash-xs font-bold text-pill-warning-fg">
                                <AlertIcon size={13} /> {s.noParent}
                              </span>
                            )}
                          </td>

                          {/* Last Played */}
                          <td className="px-5 py-4 font-medium text-muted">
                            {describeLastPlayed(row.lastPlayed, t.never).date}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {/* Footer Count Summary */}
            <p className="mt-6 flex items-center gap-2 font-dash text-dash-xs font-medium text-muted">
              <ChildrenIcon size={15} />
              <span>{t.totalChildren(total, groups.length)}</span>
            </p>
          </>
        )}
      </DataSurface>
    </section>
  )
}

export interface StudentGroup {
  key: string
  heading: string
  students: StudentRow[]
}

/**
 * Group into grade · section, preserving the server's order.
 *
 * ⚠️ EXPORTED AND PURE SO IT CAN BE TESTED. The repo has no DOM renderer
 * (OD-60), so a grouping rule written inside the component is a rule no test can
 * reach — and this one decides which heading a child appears under.
 */
export function groupStudents(rows: readonly StudentRow[], query: string): StudentGroup[] {
  const q = query.trim().toLowerCase()
  const matches = (r: StudentRow): boolean =>
    q === '' ||
    r.name.toLowerCase().includes(q) ||
    (r.rollNumber ?? '').toLowerCase().includes(q) ||
    /*
     * Searching "2B" should find the section, not just a child called 2B. A
     * teacher who knows the class but not the name is the common case on this
     * screen.
     */
    (r.grade ?? '').toLowerCase().includes(q) ||
    (r.section ?? '').toLowerCase().includes(q) ||
    r.className.toLowerCase().includes(q)

  const out: StudentGroup[] = []
  const index = new Map<string, StudentGroup>()

  for (const row of rows) {
    if (!matches(row)) continue
    /*
     * Keyed on the CLASS ID, not on the printed heading. Two sections could
     * legitimately print the same label — a school with an ungraded
     * "Butterflies" in two academic years, say — and merging them would put two
     * classes' children under one heading with duplicate roll numbers.
     */
    const existing = index.get(row.classId)
    if (existing) {
      existing.students.push(row)
      continue
    }
    const group: StudentGroup = {
      key: row.classId,
      heading: headingFor(row),
      students: [row],
    }
    index.set(row.classId, group)
    // Push in first-seen order, which is the server's grade → section ordering.
    out.push(group)
  }

  return out
}

/**
 * "Class 2 · B", falling back to the class name.
 *
 * 🔴 NEVER A DANGLING SEPARATOR. Both columns are nullable — every row created
 * before the migration has both null, and a single-stream nursery has no
 * section — so a naive template renders "Class 2 · " or " · B", which reads as
 * data that failed to load.
 */
function headingFor(row: StudentRow): string {
  const parts = [row.grade, row.section].filter(
    (p): p is string => p !== null && p.trim() !== '',
  )
  return parts.length > 0 ? parts.join(' · ') : row.className
}
